import { Toast } from '../../../../components/Common/Toast';
import { HTTPStatusEnum } from '../../../../interfaces/log.interface';
import { callAPI } from '../../../../services/api.service';
import { TokenCookieManager } from '../../../../utils/tokenCookies';
import { getImpersonationToken, shouldUseImpersonationAuth } from '../../../../utils/impersonation.utils';
import { sseUpload, type ImportProgressData } from '../../../../utils/sseUpload';

export interface DistrictModel {
  id: string;
  name: string;
  externalId?: string;
  stateExternalId?: string;
  country: string;
  state: string;
  stateName?: string;
  city?: string;
  zip?: string;
  address?: string;
  phone?: string;
  website?: string;
  districtType?: string;
  isCharter?: boolean;
  gradeRange?: { low: string; high: string };
  operationalSchools?: number;
  cleverId?: string;
  source: string;
  sourcePriority: number;
  status?: string;
  isActive: boolean;
  createdAt?: any;
  updatedAt?: any;
}

export const getAdminDistricts = async (
  setDistricts: (districts: DistrictModel[]) => void,
  setTotalPages: (totalPages: number) => void,
  page?: number,
  state?: string,
  search?: string,
  source?: string,
  setTotalRecords?: (totalRecords: number) => void,
) => {
  try {
    const params = new URLSearchParams();
    if (page) params.set('page', String(page));
    if (state) params.set('state', state);
    if (search) params.set('search', search);
    if (source) params.set('source', source);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await callAPI(`admin/district${qs}`);
    if (response?.districts) {
      setDistricts(response.districts);
      setTotalPages(response.totalPages ?? 0);
      setTotalRecords?.(response.totalRecords ?? 0);
    } else {
      Toast('Failed to get districts', true);
    }
  } catch (error) {
    Toast('Failed to get districts', true);
  }
};

export const saveDistrict = async (
  data: Partial<DistrictModel>,
  onSuccess: () => void
) => {
  try {
    const response = await callAPI('admin/district/save', data);
    if (response?.status === HTTPStatusEnum.OK) {
      Toast('District created successfully');
      onSuccess();
    } else {
      Toast(response?.message || 'Failed to create district', true);
    }
  } catch (error) {
    Toast('Failed to create district', true);
  }
};

export const updateDistrict = async (
  data: Partial<DistrictModel> & { id: string },
  onSuccess: () => void
) => {
  try {
    const response = await callAPI('admin/district/update', data);
    if (response?.status === HTTPStatusEnum.OK) {
      Toast('District updated successfully');
      onSuccess();
    } else {
      Toast(response?.message || 'Failed to update district', true);
    }
  } catch (error) {
    Toast('Failed to update district', true);
  }
};

export const deleteDistrict = async (id: string, onSuccess: () => void) => {
  try {
    const response = await callAPI('admin/district/delete', { id });
    if (response?.status === HTTPStatusEnum.OK) {
      Toast('District deleted successfully');
      onSuccess();
    } else {
      Toast(response?.message || 'Failed to delete district', true);
    }
  } catch (error) {
    Toast('Failed to delete district', true);
  }
};

export const changeDistrictStatus = async (id: string, onSuccess: () => void) => {
  try {
    const response = await callAPI('admin/district/status', { id });
    if (response?.status === HTTPStatusEnum.OK) {
      Toast('District status updated successfully');
      onSuccess();
    } else {
      Toast(response?.message || 'Failed to update district status', true);
    }
  } catch (error) {
    Toast('Failed to update district status', true);
  }
};

export const importDistrictsCsv = async (
  file: File,
  onProgress?: (data: ImportProgressData) => void,
): Promise<{ success: boolean; message: string }> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const headers: Record<string, string> = {};
    if (shouldUseImpersonationAuth()) {
      const token = getImpersonationToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    }

    return await sseUpload(
      `${process.env.REACT_APP_API_URL}/admin/district/import`,
      formData,
      onProgress,
      headers,
    );
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      return { success: false, message: 'Import timed out. The file may be too large or the server is busy. Please try again.' };
    }
    return { success: false, message: 'Network error while importing districts. Please check your connection and try again.' };
  }
};

export const deleteAllDistricts = async (): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await callAPI('admin/district/delete-all', null);
    if (response?.status === HTTPStatusEnum.OK) {
      return { success: true, message: response.message || 'All districts deleted' };
    } else {
      return { success: false, message: response?.message || 'Failed to delete all districts' };
    }
  } catch (error) {
    return { success: false, message: 'Failed to delete all districts' };
  }
};
